import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, ConfigProvider, Form, message, Modal, Select, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { useClashWarning } from "components/modals/ClashWarningModal";
import WindowConflictModal, {
  WindowConflictHint,
} from "components/modals/WindowConflictModal.component";
import {
  ModalHeader,
  modalButtonsTheme,
  outlinedButtonTheme,
} from "components/modals/ModalChrome";
import { useBookOpenClassV2 } from "hooks/mutations/booking-v2";
import { getCustomerPackageByClassCustomerId } from "hooks/queries/customerpackage";
import { availableCustomersByScheduleV2Options } from "hooks/queries/schedule-v2";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import {
  personFilterOption,
  renderPersonOption,
} from "shared/components/PersonSelectOption";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { creditCostSuffix, getActualRemainingCredits, PRODUCT_TYPE_LABELS, SHARED_LABEL } from "shared/utils";
import { buildCustomerPickResumeDraft } from "shared/utils/scheduleResumeDraft";
import { DEFAULT_DATE_SUBMIT_FORMAT, tzDate } from "shared/utils/dayjs";
import { BookScheduleForm, WindowConflict } from "shared/types";
import { textPx } from "shared/constants/zoom";

interface NewBookingModalProps {
  scheduleId: string;
  occurrenceTimestamp: string;
  classId: string;
  disabled: boolean;
  // Reopen with this customer pre-selected after a round trip to their detail
  // page to buy a package (see the "Kelola Paket Klien" empty-state below) —
  // set by ScheduleDetails.tsx's resume effect, consumed once then cleared.
  resumeCustomerId?: string;
  onResumeConsumed?: () => void;
}

const NewBookingModal = ({
  scheduleId,
  occurrenceTimestamp,
  classId,
  disabled,
  resumeCustomerId,
  onResumeConsumed,
}: NewBookingModalProps) => {
  const navigate = useNavigate();
  const [form] = useForm();
  const [isNewBookingModalOpen, setNewBookingModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string>();
  const { data: customers, isLoading: customerLoading } = useQuery(
    availableCustomersByScheduleV2Options(
      { schedule_id: scheduleId, occurrence_timestamp: occurrenceTimestamp },
      { enabled: isNewBookingModalOpen && !disabled },
    ),
  );
  const { data: customerPackages, isLoading: customerPackagesLoading } = getCustomerPackageByClassCustomerId({
    customer_id: selectedCustomer,
    class_id: classId,
    // Packages valid for THIS occurrence, not for today: a walk-in added to a
    // class that already ran (incl. a backdated one) is paid for by the package
    // that covered that date, even if it has since expired.
    occurrence_date: tzDate(occurrenceTimestamp).format(DEFAULT_DATE_SUBMIT_FORMAT),
    // Also a package this class would cut short, marked: picking it opens the
    // swap / extend choice (WindowConflictModal).
    include_conflicts: "true",
  }, { enabled: isNewBookingModalOpen });
  // A package the class would cut short: the refusal carries the conflict,
  // and the admin's swap / extend choice retries with on_conflict.
  const [conflict, setConflict] = useState<{
    conflict: WindowConflict;
    form: BookScheduleForm;
  } | null>(null);
  const { mutate: bookOpenClass, isSuccess } = useBookOpenClassV2((c, form) =>
    setConflict({ conflict: c, form }),
  );
  const { checkAndConfirm, clashWarning } = useClashWarning({
    clientReasonKey: "clash.reason_client_booking",
  });

  // Resuming after a round trip to a client's detail page to buy a package
  // (see handleBuyPackage below) — reopen with that customer pre-selected.
  useEffect(() => {
    if (!resumeCustomerId) return;
    setNewBookingModalOpen(true);
    setSelectedCustomer(resumeCustomerId);
    form.setFieldValue("customer_id", resumeCustomerId);
    onResumeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeCustomerId]);

  // A customer with no valid package for this class can't be booked from
  // here — send the admin to manage their packages, carrying enough state to
  // reopen this modal with them pre-selected on return (see the resume
  // effect above and ScheduleDetails.tsx's resume effect, which threads the
  // draft down through PrimaryAction).
  const handleBuyPackage = (customerId: string) => {
    navigate(`${PATHS.CUSTOMER}/${customerId}`, {
      state: {
        scheduleDraft: buildCustomerPickResumeDraft(
          "new-booking",
          `${PATHS.SCHEDULE}/${scheduleId}?occurrence_timestamp=${encodeURIComponent(occurrenceTimestamp)}`,
          customerId,
        ),
      },
    });
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const book = () =>
          bookOpenClass({
            customer_id: values.customer_id,
            customer_package_id: values.customer_package_id,
            schedule_id: scheduleId,
            occurrence_timestamp: occurrenceTimestamp,
          });
        // Allowed-with-warning: warn if the customer already has an overlapping
        // active booking, then book on confirm. No instructor dimension here.
        checkAndConfirm(
          {
            class_id: classId,
            customer_ids: [values.customer_id],
            start_time: occurrenceTimestamp,
          },
          book,
        );
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
      });
  };

  const handleCustomerChange = (value: string) => {
    setSelectedCustomer(value);
    // Clear the previously-picked package: the option list refetches for the
    // new customer, so the old selection may not be in it. This used to be
    // about never pairing one customer with another customer's package; with
    // package sharing that pairing is legitimate, but only when the server says
    // the client owns the package OR is an active member of it, which is
    // exactly what the refetched list reflects.
    form.setFieldValue("customer_package_id", undefined);
  };

  const handleExitModal = () => {
    setNewBookingModalOpen(!isNewBookingModalOpen);
    form.resetFields();
    setSelectedCustomer(undefined);
  };

  useEffect(() => {
    if (isSuccess) {
      handleExitModal();
    }
    // eslint-disable-next-line
  }, [isSuccess]);

  return (
    // Popup button-color system: navy "+ Booking Baru" trigger and "Book"
    // confirm, solid-ink "Batal" dismiss.
    <ConfigProvider theme={modalButtonsTheme}>
      <Button
        onClick={() => {
          setNewBookingModalOpen(true);
        }}
        type="primary"
        icon={<PlusOutlined />}
        disabled={disabled}
      >
        {translate("button.new_booking")}
      </Button>
      <Modal
        title={<ModalHeader title={translate("title.new_booking")} />}
        open={isNewBookingModalOpen}
        onOk={onSubmit}
        onCancel={handleExitModal}
        okText="Book"
        width={420}
      >
        <Form form={form} layout="vertical" name="newBookingForm">
          <Form.Item
            name="customer_id"
            label={translate("customer")}
            rules={[
              {
                required: true,
                message: `${translate("form.error.select")} ${translate("customer")}`,
              },
            ]}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate("customer")}`}
              loading={customerLoading}
              onChange={handleCustomerChange}
              showSearch
              filterOption={personFilterOption}
              optionRender={renderPersonOption}
              options={customers?.map((customer) => {
                return {
                  value: customer.id,
                  label: customer.full_name,
                  phone: customer.phone_number,
                };
              })}
              notFoundContent={translate("form.error.empty.available_customer_booking")}
            />
          </Form.Item>
          <Form.Item
            name="customer_package_id"
            label={translate("label.customer_package")}
            rules={[{ required: true, message: `${translate("form.error.select")} ${translate("label.customer_package")}` }]}
          >
            <Select
              placeholder={`${translate("form.placeholders.select")} ${translate("label.customer_package")}`}
              loading={customerPackagesLoading}
              optionLabelProp="label"
              // Also disabled once we know there's nothing to pick (empty,
              // done loading) — otherwise it renders as a live, clickable
              // field right above the "no valid package" notice below.
              disabled={
                !selectedCustomer ||
                (!customerPackagesLoading && customerPackages?.length === 0)
              }
            >
              {customerPackages && customerPackages.length > 0 ? (
                customerPackages.map((pkg) => (
                  <Select.Option
                    key={pkg.id}
                    value={pkg.id}
                    // The field shows just the package; the conflict note
                    // stays in the open list.
                    label={
                      pkg.package_deal.unlimited
                        ? pkg.package_deal.name
                        : `${pkg.package_deal.name} - [${translate("customer_package.remaining")}: ${getActualRemainingCredits(pkg)}/${pkg.total_credits}]${creditCostSuffix(pkg)}`
                    }
                  >
                    {/* A shared package is otherwise indistinguishable from
                        the client's own, and it spends someone else's credits,
                        so the owner is named. */}
                    {pkg.is_shared_with_me && (
                      <span style={{ marginRight: 8 }}>
                        <StatusBadge
                          tone="progress"
                          label={SHARED_LABEL}
                          dot={false}
                        />
                      </span>
                    )}
                    {pkg.package_deal.unlimited ? (
                      <>
                        {pkg.package_deal.name}
                        <span style={{ marginLeft: 8 }}>
                          <StatusBadge tone="navy" label={PRODUCT_TYPE_LABELS.membership} dot={false} />
                        </span>
                      </>
                    ) : (
                      `${pkg.package_deal.name} - [${translate("customer_package.remaining")}: ${getActualRemainingCredits(pkg)}/${pkg.total_credits}]${creditCostSuffix(pkg)}`
                    )}
                    <WindowConflictHint pkg={pkg} />
                  </Select.Option>
                ))
              ) : (
                <Select.Option disabled>{translate("form.empty.customer_package")}</Select.Option>
              )}
            </Select>
          </Form.Item>
          {selectedCustomer && !customerPackagesLoading && customerPackages?.length === 0 && (
            <div
              style={{
                marginTop: -4,
                padding: "12px 14px",
                borderRadius: 12,
                background: colors.bgSoft,
                border: `1px solid ${colors.line}`,
              }}
            >
              <Typography.Text
                style={{ display: "block", fontSize: textPx(13), color: colors.ink2, marginBottom: 10 }}
              >
                {translate("schedule.drawer.no_valid_package")}
              </Typography.Text>
              {/* This modal's outer ConfigProvider (modalButtonsTheme) makes
                  default buttons solid ink for the footer's Batal —
                  outlinedButtonTheme restores the app's normal outlined
                  button (incl. hover/active) for this secondary action, so
                  it matches ClientPackageSection.tsx's. */}
              <ConfigProvider theme={outlinedButtonTheme}>
                <Button size="small" onClick={() => handleBuyPackage(selectedCustomer)}>
                  {translate("schedule.drawer.manage_client_packages")}
                </Button>
              </ConfigProvider>
            </div>
          )}
        </Form>
      </Modal>
      {clashWarning}
      <WindowConflictModal
        conflict={conflict?.conflict ?? null}
        onCancel={() => setConflict(null)}
        onChoose={(choice) => {
          if (conflict) bookOpenClass({ ...conflict.form, on_conflict: choice });
          setConflict(null);
        }}
      />
    </ConfigProvider>
  );
};

export default NewBookingModal;
